import { useMutation, useQueryClient } from '@tanstack/react-query';
import request from '@teetsh/app/src/utils/request';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import {
  DomaineSocleCommunPositionnement,
  ILivretStatus,
  LivretEval,
  ModaliteAccompagnement,
} from '../types/livret';
import livretKeys from './keys';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import { useTranslation } from 'react-i18next';

interface Props {
  livretEleveId: string;
  livretEvalId: string;
}

interface CompetenceNumeriqueDTO {
  code: string;
  niveau?: 1 | 2 | 3;
}

interface UpdateLivretEleveDTO {
  id: string;
  status?: ILivretStatus;
  bilan?: string;
  competencesNumerique?: CompetenceNumeriqueDTO[];
  socle?: DomaineSocleCommunPositionnement[];
  modalitesAccompagnement?: ModaliteAccompagnement[];
  appreciationNumerique?: string;
}

function updateLivretEleve({
  schoolyearId,
  schoolId,
  livretEleveId,
  livretEleve,
}: {
  schoolyearId: string;
  schoolId: string;
  livretEleveId: string;
  livretEleve: UpdateLivretEleveDTO;
}) {
  const requestURL = `/schoolyears/${schoolyearId}/schools/${schoolId}/livrets-eleves/${livretEleveId}`;
  return request(requestURL, {
    method: 'PATCH',
    body: {
      id: livretEleve.id,
      status: livretEleve.status,
      bilan: livretEleve.bilan,
      appreciationNumerique: livretEleve.appreciationNumerique,
      competencesNumerique: livretEleve.competencesNumerique,
      socle: livretEleve.socle,
      modalitesAccompagnement: livretEleve.modalitesAccompagnement,
    },
  });
}

export function useUpdateLivretEleve(props: Props) {
  const { notifyError } = useNotification();
  const { livretEleveId, livretEvalId } = props;
  const { schoolyear, school } = useSchoolSchoolyear();
  const queryClient = useQueryClient();
  const { t } = useTranslation(['carnet']);
  const { mutateAsync } = useMutation({
    mutationFn: (livretEleve: UpdateLivretEleveDTO) =>
      updateLivretEleve({
        schoolyearId: schoolyear.id,
        schoolId: school.id,
        livretEleveId,
        livretEleve,
      }),
    onMutate: lEleve => {
      queryClient.setQueryData<LivretEval>(
        livretKeys.livretDetail(schoolyear.id, school.id, livretEvalId),
        (oldLivretEval: LivretEval) => ({
          ...oldLivretEval,
          livretsEleves: (oldLivretEval || []).livretsEleves.map(
            livretEleve => {
              if (livretEleve.id === livretEleveId) {
                return {
                  ...livretEleve,
                  ...lEleve,
                };
              }
              return livretEleve;
            },
          ),
        }),
      );
    },
    onSuccess: d => {
      queryClient.setQueryData<LivretEval>(
        livretKeys.livretDetail(schoolyear.id, school.id, livretEvalId),
        (oldLivretEval: LivretEval) => ({
          ...oldLivretEval,
          livretsEleves: (oldLivretEval || []).livretsEleves.map(
            livretEleve => {
              if (livretEleve.id === livretEleveId) {
                return {
                  ...livretEleve,
                  lsuErrors: d.errors,
                };
              }
              return livretEleve;
            },
          ),
        }),
      );
    },
    onError: () => {
      notifyError({
        title: t('carnet:messages.livret.updateError'),
      });
    },
  });
  return mutateAsync;
}
